// src/services/vibeCommentApi.js — the browser's calls to the comment routes.
//
// The server writes comments now: the safety check runs before a comment lands,
// and the notifications (bell + push, with the comment id) are written there.
// See server/functions/commentRoutes.js; the phone's twin is
// services/vibeCommentApi.ts.
//
// 🟥 TWO KINDS OF "NO". A 4xx/5xx is the server ANSWERING (reply target gone,
//    comments off, too long) and is shown. A network failure is the server NOT
//    REACHED, and the caller falls back to the direct write it always did; the
//    server's sweep judges that comment later. `status` is set only in the first
//    case.
import { auth } from '../config/firebase';
import { BACKEND_URL } from './socket';
import { useEffect, useState } from 'react';
import { createCommentPager } from './commentPages';
import { createCommentMine, reactionNeedsMine } from './vibeComments';

async function call(path, body) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  let res;
  try {
    res = await fetch(`${BACKEND_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ...body, platform: 'web' }),
    });
  } catch { return { ok: false }; }
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (!res.ok || !j || j.ok === false) return { ok: false, status: res.status || 500, error: (j && j.error) || `http ${res.status}` };
  return { ok: true, comment: j.comment, notified: j.notified };
}

/** Write a comment (or a reply — `replyToId` is the exact comment answered). */
/* `id` is the composer's own — the server keeps it, so the local row and the
   document's copy are one comment, and a retry is recognised, not doubled */
/* `mentions`/`mentionNames`: who the author picked after "@" — the server keeps
   only the ones the words still contain (services/commentMentions) */
export const postVibeComment = ({ postId, text, replyToId, id, mentions, mentionNames }) => call('/api/vibes/comment', {
  postId, text, replyToId: replyToId || null, ...(id ? { id: String(id) } : {}),
  ...(Array.isArray(mentions) && mentions.length ? { mentions, mentionNames: mentionNames || {} } : {}),
});
/** Edit your own comment; it is judged again. */
export const editVibeComment = ({ postId, commentId, text, mentions, mentionNames }) => call('/api/vibes/comment/edit', {
  postId, commentId, text, ...(Array.isArray(mentions) ? { mentions, mentionNames: mentionNames || {} } : {}),
});

/** 👍👎 Agree, or not — pressing what you already pressed takes it back. */
export const reactVibeComment = ({ postId, commentId, kind }) => call('/api/vibes/comment/react', { postId, commentId, kind });

/** 🗑️ Remove a comment (and what answered it). The server decides who may. */
export async function deleteVibeComment({ postId, commentId }) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api/vibes/comment/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ postId, commentId }),
    });
  } catch { return { ok: false }; }
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (!res.ok || !j || j.ok === false) return { ok: false, status: res.status || 500, error: (j && j.error) || `http ${res.status}` };
  return { ok: true, removed: Array.isArray(j.removed) ? j.removed.map(String) : [String(commentId)] };
}

/** 📌 Pin a top-level comment, or un-pin (`commentId: null`) — the post's owner only, one per post.
 *  Says what the post now has pinned, and the comment itself (so a paged thread can draw it). */
export async function pinVibeComment({ postId, commentId }) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api/vibes/comment/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ postId, commentId: commentId ? String(commentId) : null }),
    });
  } catch { return { ok: false }; }
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (!res.ok || !j || j.ok === false) return { ok: false, status: res.status || 500, error: (j && j.error) || `http ${res.status}` };
  return { ok: true, pinnedCommentId: j.pinnedCommentId ? String(j.pinnedCommentId) : null, comment: j.comment || null };
}

/* ═══ 📄 A PAGE AT A TIME ════════════════════════════════════════════════════
   Comments are documents now (services/commentPages — generated from the
   phone's TypeScript). The thread asks the server for twenty threads, then
   twenty more as the reader scrolls, then only what is new when the post's
   counter moves. One pager for the tab, so the pages outlive the thread. */
export async function fetchCommentPage(q) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  let res;
  try {
    res = await fetch(`${BACKEND_URL}/api/vibes/comments/page`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(q),
    });
  } catch { return { ok: false }; }
  let j = null;
  try { j = await res.json(); } catch { j = null; }
  if (!res.ok || !j || j.ok === false) return { ok: false, status: res.status || 500 };
  return { ok: true, items: Array.isArray(j.items) ? j.items : [], next: j.next || null, done: !!j.done };
}

export const commentPager = createCommentPager(fetchCommentPage);

/* ═══ 👍👎 "WHAT DID I PRESS?" PAST THE LEDGER ════════════════════════════
   A comment thumbed by more than COMMENT_REACT_LEDGER_CAP people cannot list
   everybody (services/vibeComments). The pages say it for their comments; an
   OLD comment (the post's array) is asked about here, batched per post. The
   phone's twin is services/vibeCommentApi.ts. */
export async function fetchMyCommentReactions(postId, commentIds) {
  let token = null;
  try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
  if (!token) return { ok: false };
  try {
    const res = await fetch(`${BACKEND_URL}/api/vibes/comment/reactions/mine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ postId, commentIds }),
    });
    const j = await res.json().catch(() => null);
    if (!res.ok || !j || j.ok === false) return { ok: false };
    return { ok: true, mine: j.mine || {} };
  } catch { return { ok: false }; }
}
export const commentMine = createCommentMine(fetchMyCommentReactions);

/** The open thread: ask about the comments that cannot answer for themselves, and re-draw when told. */
export function useCommentMine(postId, comments, uid) {
  const [v, bump] = useState(0);
  const pid = String(postId || '');
  const need = uid && pid ? (comments || []).filter((c) => reactionNeedsMine(c, uid)) : [];
  const key = need.map((c) => String(c.id)).join(',');
  useEffect(() => {
    if (uid) commentMine.setMe(uid);
    for (const c of need) commentMine.want(pid, c);
  }, [pid, uid, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => commentMine.subscribe(() => bump((n) => (n + 1) % 1e9)), []);
  return v;
}
